import * as moment from 'moment';
import { Nullable, ValueOf } from 'global';
import { invoiceCalculations } from 'modules/invoice';
import { VendorOpportunity } from 'modules/vendorOpportunity';
import { equipmentMileageFormatter } from './equipmentMileageFormatter';
import {
  CcEmailOption,
  CcEmailOptionGroups,
  Equipment,
  FinanceProgramFormValues,
  MarkupMethods,
  MarkupType,
  PaymentCalculationMethods,
  ProgramTypes,
  RateTypes,
  SaleType,
  SendVia,
  Structures,
} from '../types';
import { EQUIPMENT_CLASS, EQUIPMENT_MAKE } from '@trnsact/business-criteria';
import { CreditApplication, PaymentType, SubsidyCapType } from '@trnsact/trnsact-shared-types';
import _ from 'lodash';

export const sendViaInitialValues = {
  type: SendVia.Email,
};

export const displaySettingsInitialValues: FinanceProgramFormValues['displaySettings'] = {
  productPrice: true,
  productDetails: true,
  showLenderLogo: false,
  showLenderName: false,
  showInterestRate: true,
  showBestOptions: false,
  shouldRedirectToOCA: true,
  excludeEquipmentSales: false,
  lenderProfileId: '',
  showFinanceProgramDescription: true,
};

export function getFormDefaultValues(
  voData: Nullable<VendorOpportunity>,
  creditAppData: any,
  equipmentData: Equipment[],
  ccEmailOptions: ValueOf<FinanceProgramFormValues, 'ccEmail'> = [],
  userEmail: string,
  defaultSaleType?: SaleType,
  displaySettings: Partial<FinanceProgramFormValues['displaySettings']> = {}
): FinanceProgramFormValues {
  const equipmentInitialValues = convertVOEquipmentInitialValues(voData, equipmentData);
  const customerInitialValues = convertVOCustomerInitialValues(creditAppData);
  const saleDetailsInitialValues = convertVOSaleDetailsInitialValues(voData, defaultSaleType);
  const financeQuoteInitialValues = convertVOFinanceQuoteInitialValues(voData);
  const ccEmailPreSelectedValue = getCcEmailPreSelectedValue(ccEmailOptions, customerInitialValues, voData, userEmail);

  return {
    sendVia: sendViaInitialValues,
    contact: customerInitialValues,
    equipment: equipmentInitialValues,
    saleDetails: saleDetailsInitialValues,
    financeQuote: financeQuoteInitialValues,
    submission: {
      selectedSubmissionId: '',
      maxApprovalAmount: null,
    },
    displaySettings: { ...displaySettingsInitialValues, ...(displaySettings || {}) },
    ccEmail: ccEmailPreSelectedValue,
    financeProgramDescription: { apiValue: '', editorValue: null },
  };
}

function getFactValueAdjustedToMatchEnum(factValue: string, enumOption: Record<string, string>): string {
  const adjustedToMatchEnumOptionKey = Object.keys(enumOption).find(key => {
    return enumOption[key as keyof typeof enumOption].toLowerCase() === factValue?.toLowerCase();
  });

  return adjustedToMatchEnumOptionKey ? enumOption[adjustedToMatchEnumOptionKey as keyof typeof enumOption] : factValue;
}

function convertVOEquipmentInitialValues(
  vo: any,
  equipmentData: Equipment[]
): ValueOf<FinanceProgramFormValues, 'equipment'> {
  const equipment = equipmentData?.[0];

  const makeOption = getFactValueAdjustedToMatchEnum(equipment?.make, EQUIPMENT_MAKE);
  const classOption = getFactValueAdjustedToMatchEnum(equipment?.classType, EQUIPMENT_CLASS);

  return {
    make: makeOption ?? '',
    class: classOption ?? '',
    year: equipment?.year ?? '',
    model: equipment?.model ?? '',
    stockId: equipment?.stockId ?? '',
    newUsed: equipment?.newUsed ?? '',
    selectedEquipment: equipment?.equipmentId ?? '',
    quantity: (equipment?.quantity ?? '').toString(),
    serialNumberVin: equipment?.serialNumberVin ?? '',
    description: equipment?.additionalDescription ?? '',
    totalWholesaleCost: (equipment?.totalWholesaleCost ?? '').toString(),
    retailPrice: vo?.equipmentCost || (equipment?.retailPrice ?? '').toString(),
    mileage: equipmentMileageFormatter(equipment?.mileage, 'toView')?.toString(),
    assets: [],
  };
}

function convertVOCustomerInitialValues(
  creditAppData: CreditApplication
): ValueOf<FinanceProgramFormValues, 'contact'> {
  const customerData = creditAppData?.primaryContact
    ? creditAppData?.primaryContact
    : (creditAppData?.creditApplicationOwner?.find(o => o!.pc) ?? creditAppData?.creditApplicationOwner?.[0]);

  const phone =
    (customerData?.phone || customerData?.mobilePhone || customerData?.homePhone || customerData?.workPhone) ?? '';

  return {
    phone,
    businessName: null,
    assignDealer: null,
    dealerLocation: null,
    email: customerData?.email ?? '',
    state: customerData?.state ?? '',
    title: customerData?.title ?? '',
    selectedContact: customerData?.ownerPgId ?? '',
    fullName: [customerData?.firstName, customerData?.lastName].filter(Boolean).join(' '),
  };
}

function convertVOSaleDetailsInitialValues(
  vo: Nullable<VendorOpportunity>,
  defaultSaleType?: SaleType
): ValueOf<FinanceProgramFormValues, 'saleDetails'> {
  const { downPaymentFlat, downPaymentPercent, netRate, totalFees, invoiceTotalValue, financeAmount } =
    invoiceCalculations.byFullVO(vo);

  return {
    netRate,
    financeAmount,
    fees: totalFees,
    invoiceTotal: invoiceTotalValue,
    salesTax: vo?.salesTaxOnInvoice ?? 0,
    equipmentSubtotal: parseFloat(vo?.equipmentCost ?? '0'),
    saleType: getDefaultSaleType(defaultSaleType, vo?.paymentType),
    downPayment: { flat: downPaymentFlat, percent: downPaymentPercent },
  };
}

function convertVOFinanceQuoteInitialValues(
  vo: Nullable<VendorOpportunity>
): ValueOf<FinanceProgramFormValues, 'financeQuote'> {
  const startDate = moment.default(new Date());
  const firstPaymentDate = moment.default(new Date()).add(30, 'days');

  return {
    fee: [],
    menu: '',
    commonRate: 0,
    terms: [],
    program: '',
    residual: '',
    markupRate: 0,
    programName: '',
    specifyRates: [],
    dealerMarkup: 0,
    advancePayment: '',
    daysToFirstPayment: '30',
    minimumRequiredDownPayment: 0,
    startDate: startDate.format('YYYY-MM-DD'),
    firstPaymentDate: firstPaymentDate.format('YYYY-MM-DD'),
    specifyRate: false,
    specifyRatePerTerm: '',
    structure: Structures.None,
    subsidyType: MarkupType.Flat,
    configureSubsidyCap: false,
    specifyLenderMarketRates: [],
    subsidyCapConfiguration: {
      lenderMarketRates: [],
      lenderName: '',
      maxSubsidyAmount: 0,
      netInvoicePercentage: 0,
      subsidyCapType: SubsidyCapType.FixedAmount,
    },
    commonLenderMarketRate: '',
    customTermTemporaryValue: null,
    programType: ProgramTypes.Loan,
    rateType: RateTypes.InterestRate,
    createProposalWithExistingProgram: null,
    markupMethod: MarkupMethods.MarkupDollar,
    amount: Number(vo?.invoiceTotal ?? 0),
    paymentCalculationMethod: PaymentCalculationMethods.Arrears,
  };
}

export function getCcEmailPreSelectedValue(
  ccEmailOptions: ValueOf<FinanceProgramFormValues, 'ccEmail'>,
  customerInitialValues: ValueOf<FinanceProgramFormValues, 'contact'>,
  vo: Nullable<VendorOpportunity>,
  userEmail: string
): ValueOf<FinanceProgramFormValues, 'ccEmail'> {
  const initialCustomerContactsOptions = ccEmailOptions.filter(
    (option: CcEmailOption) =>
      option.group === CcEmailOptionGroups.CustomerContacts && option.email !== customerInitialValues.email
  );

  const initialSalesRepOption = ccEmailOptions.find(
    (option: CcEmailOption) =>
      option.group === CcEmailOptionGroups.DealershipContacts && option.email === vo?.salesRepresentative?.email
  );

  const initialCurrentUserProfileOption = ccEmailOptions.find((option: CcEmailOption) => option.email === userEmail);

  return _([...initialCustomerContactsOptions, ...[initialSalesRepOption], ...[initialCurrentUserProfileOption]])
    .filter((option?: CcEmailOption) => !!option)
    .uniqBy('email')
    .value();
}

function getDefaultSaleType(defaultSaleType: SaleType | undefined, voPaymentType: string | undefined): SaleType {
  if (defaultSaleType) return defaultSaleType;

  if (voPaymentType) {
    return voPaymentType === PaymentType.Financing ? SaleType.Financing : SaleType.Cash;
  }

  return SaleType.Financing;
}
